import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin, Phone, Truck, Check, ArrowRight } from "lucide-react";
import Seo, { localBusinessJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, ProductCard, StarRating } from "@/components/common";
import { getCity, PRODUCTS, BRANDS, SITE } from "@/mock";

export default function CityLanding() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <Navigate to="/locations" replace />;
  const featured = PRODUCTS.filter((p) => p.category === "mattresses").slice(0, 4);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Location", path: "/locations" },
    { name: `${city.city} Mattress Store`, path: `/mattress-store/${city.slug}` },
  ];

  return (
    <>
      <Seo
        title={`Mattress Store in ${city.city}, FL | Delivery`}
        description={`Looking for a mattress store near ${city.city}, ${city.county}? MattressTown offers premium Beautyrest, Puffy & Serta mattresses with same-day delivery to ${city.city}.`}
        path={`/mattress-store/${city.slug}`}
        jsonLd={[localBusinessJsonLd(), breadcrumbJsonLd(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold uppercase leading-tight text-neutral-900">
          Mattress Store Serving {city.city}, FL
        </h1>
        <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-neutral-700"><StarRating value={5} size={14} /> {SITE.rating.toFixed(1)} · {SITE.reviewCount} Reviews</p>
        <p className="mt-5 max-w-3xl leading-relaxed text-neutral-600">{city.intro}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"><Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}</a>
          <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-7 py-3 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white"><MapPin className="h-4 w-4" aria-hidden="true" /> Visit Showroom</a>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold uppercase text-neutral-900">Why {city.city} Shops at MattressTown</h2>
            <ul className="mt-4 space-y-3 text-neutral-700">
              {[
                `Same-day delivery, set-up & removal to ${city.city} homes`,
                "Premium brands: Beautyrest Black, Puffy & Serta",
                "Family-owned with honest, no-pressure pricing",
                "Low price guarantee & special financing available",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-5 w-5 shrink-0 text-[#D0021B]" aria-hidden="true" /> {t}</li>
              ))}
            </ul>

            <h2 className="mt-10 text-2xl font-bold uppercase text-neutral-900">Neighborhoods We Serve in {city.city}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {city.neighborhoods.map((n) => (
                <span key={n} className="rounded-full bg-neutral-100 px-4 py-1.5 text-sm font-medium text-neutral-700">{n}</span>
              ))}
            </div>
          </div>

          <aside className="rounded-2xl border border-neutral-200 bg-cream p-6">
            <Truck className="h-8 w-8 text-[#D0021B]" aria-hidden="true" />
            <h2 className="mt-3 text-xl font-bold uppercase text-neutral-900">Delivering to {city.city}</h2>
            <p className="mt-2 text-sm text-neutral-600">From our Fort Lauderdale showroom at {SITE.address.full}. Order today, sleep on it tonight.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {BRANDS.map((b) => (<Link key={b.slug} to={`/brands/${b.slug}`} className="rounded-full border border-neutral-300 px-3 py-1 text-xs font-semibold text-neutral-700 hover:border-[#D0021B] hover:text-[#D0021B]">{b.name}</Link>))}
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <div className="flex items-end justify-between">
            <h2 className="text-2xl font-bold uppercase text-neutral-900">Best Sellers for {city.city}</h2>
            <Link to="/mattresses" className="text-sm font-bold uppercase text-[#D0021B] hover:underline">Shop All →</Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-4">{featured.map((p) => (<ProductCard key={p.slug} product={p} />))}</div>
        </section>

        <div className="mt-12 rounded-2xl bg-neutral-900 p-8 text-center text-white">
          <h2 className="text-2xl font-bold uppercase">Ready for Better Sleep in {city.city}?</h2>
          <p className="mx-auto mt-2 max-w-xl text-neutral-300">Call or visit our Fort Lauderdale showroom today - our family will help you find the perfect mattress.</p>
          <a href={SITE.phoneHref} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]">Call {SITE.phone} <ArrowRight className="h-4 w-4" aria-hidden="true" /></a>
        </div>
      </div>
    </>
  );
}
